import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { fmtTime, STATUS_LABEL } from "@/components/partner/questionsUtil";

function InboxItem({ q, onChange }) {
    const [reply, setReply] = useState(q.owner_reply || "");
    const [busy, setBusy] = useState(false);

    const post = async (path, body, msg) => {
        setBusy(true);
        try {
            await api.post(`/owner/partner-questions/${q.id}/${path}`, body);
            toast.success(msg);
            onChange();
        } catch {
            toast.error("Could not update");
        } finally {
            setBusy(false);
        }
    };

    return (
        <li className="p-4 border border-cream/20 rounded-sm text-sm text-cream" data-testid={`owner-pq-${q.id}`}>
            <div className="flex flex-wrap justify-between gap-2">
                <span className="font-semibold">{!q.owner_read ? "● " : ""}{q.partner_name}</span>
                <span>{fmtTime(q.created_at)}</span>
            </div>
            <p className="mt-1 text-xs">
                {STATUS_LABEL[q.status]} · {q.escalation_reason === "flagged" ? "flagged by partner" : "assistant couldn't answer"}
            </p>
            <p className="mt-3 whitespace-pre-wrap">{q.question}</p>
            {q.ai_answer && <p className="mt-2 text-xs whitespace-pre-wrap">Assistant said: {q.ai_answer}</p>}
            {q.replied_at && <p className="mt-2 text-xs">Replied {fmtTime(q.replied_at)}</p>}
            <textarea rows={3} value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Write your reply…"
                className="mt-3 w-full bg-transparent border border-cream/30 rounded-sm px-3 py-2 text-sm text-cream"
                data-testid={`owner-pq-reply-input-${q.id}`} />
            <div className="mt-2 flex flex-wrap gap-2">
                <button disabled={busy || !reply.trim()} onClick={() => post("reply", { reply, mark_answered: true }, "Reply sent")}
                    className="btn-pill btn-ghost text-[0.6rem]" data-testid={`owner-pq-reply-btn-${q.id}`}>
                    {busy ? <Loader2 size={12} className="animate-spin" /> : null} Reply &amp; mark answered
                </button>
                <button disabled={busy} onClick={() => post("status", { answered: q.status !== "answered", read: true }, "Updated")}
                    className="btn-pill btn-ghost text-[0.6rem]" data-testid={`owner-pq-status-btn-${q.id}`}>
                    {q.status === "answered" ? "Reopen" : "Mark answered"}
                </button>
                {!q.owner_read && (
                    <button disabled={busy} onClick={() => post("status", { read: true }, "Marked read")}
                        className="btn-pill btn-ghost text-[0.6rem]" data-testid={`owner-pq-read-btn-${q.id}`}>
                        Mark read
                    </button>
                )}
            </div>
        </li>
    );
}

export default function PartnerQuestionsInbox() {
    const [data, setData] = useState(null);
    const load = () => api.get("/owner/partner-questions").then(({ data }) => setData(data)).catch(() => toast.error("Could not load questions."));
    useEffect(() => { load(); }, []);
    if (!data) return <Loader2 className="animate-spin mt-8" size={18} />;
    return (
        <div className="mt-6 max-w-3xl" data-testid="owner-partner-questions">
            <p className="text-sm text-cream" data-testid="owner-pq-count">
                {data.questions.length} question{data.questions.length === 1 ? "" : "s"} · {data.open} open
            </p>
            {data.questions.length === 0 && <p className="mt-4 text-sm text-cream">No partner questions yet.</p>}
            <ul className="mt-4 space-y-3">
                {data.questions.map((q) => <InboxItem key={q.id} q={q} onChange={load} />)}
            </ul>
        </div>
    );
}
